// src/components/HistorySection.js
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHistory,
    faChevronDown,
    faChevronUp,
    faChevronLeft,
    faChevronRight,
    faPlus,
    faPencilAlt,
    faTrashAlt,
    faUsers,
    faUser
} from '@fortawesome/free-solid-svg-icons';
import { db } from '../firebase';
import { collection, query, where, orderBy, getDocs, limit } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { formatDistanceToNow, format, isThisYear } from 'date-fns';

import './HistorySection.css';

const PAGE_SIZE = 10;

// Format a Firestore timestamp to a readable string
const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    // Within last 24 hours: "2 hours ago"
    if (diffInHours < 24) {
        return formatDistanceToNow(date, { addSuffix: true });
    }

    // Within this year: "Jan 15"
    if (isThisYear(date)) {
        return format(date, 'MMM d');
    }

    // Older: "Jan 15, 2024"
    return format(date, 'MMM d, yyyy');
};

// Format field name to human-readable
const formatFieldName = (field) => {
    const fieldMappings = {
        'name': 'Name',
        'surname': 'Last Name',
        'street': 'Street',
        'city': 'City',
        'state': 'State',
        'zipcode': 'Zip Code',
        'phone': 'Phone',
        'cellphone': 'Cell Phone',
        'email': 'Email',
        'birthdayMonth': 'Birthday Month',
        'birthdayDay': 'Birthday Day',
        'birthdayYear': 'Birthday Year',
        'anniversaryMonth': 'Anniversary Month',
        'anniversaryDay': 'Anniversary Day',
        'anniversaryYear': 'Anniversary Year',
        'photo': 'Photo',
        'familyId': 'Family',
    };
    return fieldMappings[field] || field.charAt(0).toUpperCase() + field.slice(1);
};

// Format a single value for display
const formatValue = (value, fieldName) => {
    if (value === null || value === undefined || value === '') {
        return '(empty)';
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    // Special handling for photo field - show only filename
    if (fieldName === 'photo' && typeof value === 'string') {
        const parts = value.split('/');
        return parts[parts.length - 1] || value;
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    // Truncate long values
    const str = String(value);
    return str.length > 50 ? str.substring(0, 47) + '...' : str;
};

// Extract meaningful changes from before/after objects
const extractChanges = (changes) => {
    if (!changes || typeof changes === 'string') {
        return null;
    }

    const { before, after } = changes;
    if (!before && !after) return null;

    const changedFields = [];

    // Fields to ignore in change display (internal/system fields)
    const ignoreFields = [
        'imageUpdatedAt', 'updatedAt', 'createdAt', 'orgId', 'id',
        'LastOriginalPhotoPathProcessed', 'MemberFacePaths', 'FacesExtractedAt',
        'lastOriginalPhotoPathProcessed', 'memberFacePaths', 'facesExtractedAt'
    ];

    // Compare before and after
    const allKeys = new Set([
        ...(before ? Object.keys(before) : []),
        ...(after ? Object.keys(after) : [])
    ]);

    for (const key of allKeys) {
        if (ignoreFields.includes(key)) continue;

        const beforeVal = before?.[key];
        const afterVal = after?.[key];

        // Only show if values are different
        if (JSON.stringify(beforeVal) !== JSON.stringify(afterVal)) {
            changedFields.push({
                field: key,
                from: beforeVal,
                to: afterVal
            });
        }
    }

    return changedFields.length > 0 ? changedFields : null;
};

// Action badge component
const ActionBadge = ({ action }) => {
    const config = {
        CREATE: { icon: faPlus, className: 'history-action-create', label: 'Created' },
        UPDATE: { icon: faPencilAlt, className: 'history-action-update', label: 'Updated' },
        DELETE: { icon: faTrashAlt, className: 'history-action-delete', label: 'Deleted' }
    };

    const { icon, className, label } = config[action] || config.UPDATE;

    return (
        <span className={`history-action-badge ${className}`}>
            <FontAwesomeIcon icon={icon} />
            {label}
        </span>
    );
};

// Entity type badge component
const EntityTypeBadge = ({ entityType }) => {
    const config = {
        FAMILY: { icon: faUsers, className: 'history-entity-family', label: 'Family' },
        MEMBER: { icon: faUser, className: 'history-entity-member', label: 'Member' }
    };

    const { icon, className, label } = config[entityType] || config.MEMBER;

    return (
        <span className={`history-entity-badge ${className}`}>
            <FontAwesomeIcon icon={icon} />
            {label}
        </span>
    );
};

function HistorySection({ entityType, entityId, includeFamilyMembers = false }) {
    const { isAdmin, userOrgId } = useAuth();
    const [isExpanded, setIsExpanded] = useState(false);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [page, setPage] = useState(1);

    // Fetch history when expanded for the first time
    useEffect(() => {
        if (!isExpanded || hasLoaded || !entityId || !entityType || !userOrgId) return;

        const fetchHistory = async () => {
            setLoading(true);
            setError(null);

            try {
                // Query for the main entity (family or member)
                const mainQuery = query(
                    collection(db, 'auditLog'),
                    where('orgId', '==', userOrgId),
                    where('entityType', '==', entityType),
                    where('entityId', '==', entityId),
                    orderBy('timestamp', 'desc'),
                    limit(50)
                );

                const mainSnapshot = await getDocs(mainQuery);
                let historyData = mainSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                // If this is a family and we want to include member changes
                if (entityType === 'FAMILY' && includeFamilyMembers) {
                    // Query for member changes that belong to this family
                    // Members store familyId in metadata.familyId
                    const memberQuery = query(
                        collection(db, 'auditLog'),
                        where('orgId', '==', userOrgId),
                        where('entityType', '==', 'MEMBER'),
                        where('metadata.familyId', '==', entityId),
                        orderBy('timestamp', 'desc'),
                        limit(50)
                    );

                    const memberSnapshot = await getDocs(memberQuery);
                    const memberHistory = memberSnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));

                    // Merge and sort by timestamp
                    historyData = [...historyData, ...memberHistory].sort((a, b) => {
                        const timeA = a.timestamp?.toMillis?.() || 0;
                        const timeB = b.timestamp?.toMillis?.() || 0;
                        return timeB - timeA;
                    });

                    // Limit to 50 most recent
                    historyData = historyData.slice(0, 50);
                }

                setHistory(historyData);
                setHasLoaded(true);
            } catch (err) {
                console.error('Error fetching history:', err);
                // Check if it's an index error
                if (err.code === 'failed-precondition' && err.message?.includes('index')) {
                    setError('History index is being created. Please try again in a few minutes.');
                } else {
                    setError('Unable to load history.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [isExpanded, hasLoaded, entityId, entityType, userOrgId, includeFamilyMembers]);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div className="history-section">
            <button
                className={`history-header ${isExpanded ? 'history-header-expanded' : ''}`}
                onClick={toggleExpanded}
                aria-expanded={isExpanded}
            >
                <div className="history-header-left">
                    <div className="history-header-icon">
                        <FontAwesomeIcon icon={faHistory} />
                    </div>
                    <h3 className="history-title">History</h3>
                    {hasLoaded && history.length > 0 && (
                        <span className="history-count">{history.length}</span>
                    )}
                </div>
                <FontAwesomeIcon
                    icon={isExpanded ? faChevronUp : faChevronDown}
                    className="history-chevron"
                />
            </button>

            {isExpanded && (
                <div className="history-body">
                    {loading ? (
                        <div className="history-loading">
                            <div className="history-loading-spinner"></div>
                            <span>Loading history...</span>
                        </div>
                    ) : error ? (
                        <div className="history-error">{error}</div>
                    ) : history.length === 0 ? (
                        <div className="history-empty">
                            <FontAwesomeIcon icon={faHistory} className="history-empty-icon" />
                            <p>No history recorded yet</p>
                        </div>
                    ) : (
                        <>
                            <div className="history-timeline">
                                {(() => {
                                    const paginatedHistory = history.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
                                    const showEntityBadge = includeFamilyMembers && entityType === 'FAMILY';

                                    return paginatedHistory.map((entry, index) => {
                                        const changes = extractChanges(entry.changes);

                                        return (
                                            <div key={entry.id} className="history-item">
                                                <div className="history-item-line">
                                                    <div className="history-item-dot"></div>
                                                    {index < paginatedHistory.length - 1 && (
                                                        <div className="history-item-connector"></div>
                                                    )}
                                                </div>
                                                <div className="history-item-content">
                                                    <div className="history-item-header">
                                                        <div className="history-item-badges">
                                                            <ActionBadge action={entry.action} />
                                                            {showEntityBadge && (
                                                                <EntityTypeBadge entityType={entry.entityType} />
                                                            )}
                                                        </div>
                                                        <span className="history-item-time">
                                                            {formatTimestamp(entry.timestamp)}
                                                        </span>
                                                    </div>

                                                    {/* Show member name for member entries on family page */}
                                                    {showEntityBadge && entry.entityType === 'MEMBER' && entry.metadata?.name && (
                                                        <div className="history-item-member-name">
                                                            {entry.metadata.name} {entry.metadata.surname || ''}
                                                        </div>
                                                    )}

                                                    {isAdmin && entry.userEmail && (
                                                        <div className="history-item-user">
                                                            by {entry.userEmail}
                                                        </div>
                                                    )}

                                                    {changes && changes.length > 0 && (
                                                        <div className="history-item-changes">
                                                            {changes.map((change, i) => (
                                                                <div key={i} className="history-change">
                                                                    <span className="history-change-field">
                                                                        {formatFieldName(change.field)}:
                                                                    </span>
                                                                    {change.from !== undefined && (
                                                                        <>
                                                                            <span className="history-change-from">
                                                                                {formatValue(change.from, change.field)}
                                                                            </span>
                                                                            <span className="history-change-arrow">→</span>
                                                                        </>
                                                                    )}
                                                                    <span className="history-change-to">
                                                                        {formatValue(change.to, change.field)}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>

                            {/* Pagination */}
                            {history.length > PAGE_SIZE && (
                                <div className="history-pagination">
                                    <button
                                        className="history-pagination-btn"
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                    </button>
                                    <span className="history-pagination-info">
                                        {page} / {Math.ceil(history.length / PAGE_SIZE)}
                                    </span>
                                    <button
                                        className="history-pagination-btn"
                                        onClick={() => setPage(p => Math.min(Math.ceil(history.length / PAGE_SIZE), p + 1))}
                                        disabled={page === Math.ceil(history.length / PAGE_SIZE)}
                                    >
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default HistorySection;
